import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Settings, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const uid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

const AdminVipSettings = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    const { data } = await axios.get(`${API}/admin/vip-settings`, { withCredentials: true });
    // Attach a stable client-only _uid to each tier so React keys stay stable across edits.
    if (Array.isArray(data?.tiers)) data.tiers = data.tiers.map((t) => ({ ...t, _uid: uid() }));
    setSettings(data);
  };

  const updateTier = (idx, field, value) => {
    const tiers = [...settings.tiers];
    tiers[idx] = { ...tiers[idx], [field]: value === '' ? null : Number(value) };
    setSettings({ ...settings, tiers });
  };

  const addTier = () => {
    setSettings({ ...settings, tiers: [...settings.tiers, { _uid: uid(), min: 0, max: null, payout: 0 }] });
  };

  const removeTier = (idx) => {
    setSettings({ ...settings, tiers: settings.tiers.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    try {
      await axios.put(`${API}/admin/vip-settings`, {
        tiers: settings.tiers,
        discount_percent: settings.discount_percent,
        allow_stacking: settings.allow_stacking,
      }, { withCredentials: true });
      toast.success('VIP settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!settings) return <div className="text-[#A3A3A3]">Loading...</div>;

  return (
    <div data-testid="admin-vip-settings-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Settings className="w-6 h-6 text-[#D4AF37]" /> VIP Payout Settings</h2>
        <p className="text-[#A3A3A3]">Configure referral bonus tiers and promo discount</p>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-5">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">Bonus Tiers</h3>
        <p className="text-[#A3A3A3] text-sm">Bonus is paid based on the final paid invoice amount.</p>
        <div className="space-y-3">
          {settings.tiers.map((t, idx) => (
            <div key={t._uid || `tier-${idx}`} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
              <div className="space-y-1">
                <Label className="text-white text-xs">Min ($)</Label>
                <Input data-testid={`tier-min-${idx}`} type="number" value={t.min ?? 0} onChange={(e) => updateTier(idx, 'min', e.target.value)} className="bg-[#141414] border-[#262626] text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-white text-xs">Max ($, blank = no cap)</Label>
                <Input data-testid={`tier-max-${idx}`} type="number" value={t.max ?? ''} onChange={(e) => updateTier(idx, 'max', e.target.value)} placeholder="No cap" className="bg-[#141414] border-[#262626] text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-white text-xs">Payout ($)</Label>
                <Input data-testid={`tier-payout-${idx}`} type="number" value={t.payout ?? 0} onChange={(e) => updateTier(idx, 'payout', e.target.value)} className="bg-[#141414] border-[#262626] text-white" />
              </div>
              <Button data-testid={`tier-remove-${idx}`} onClick={() => removeTier(idx)} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
            </div>
          ))}
        </div>
        <Button data-testid="add-tier-btn" onClick={addTier} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Plus className="w-4 h-4 mr-1" /> Add Tier</Button>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">Discount Rules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-white text-xs">Promo Code Discount %</Label>
            <Input data-testid="discount-percent" type="number" value={settings.discount_percent} onChange={(e) => setSettings({...settings, discount_percent: Number(e.target.value)})} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <label className="flex items-center gap-2 text-white text-sm mt-6">
            <input data-testid="allow-stacking" type="checkbox" checked={settings.allow_stacking} onChange={(e) => setSettings({...settings, allow_stacking: e.target.checked})} className="accent-[#D4AF37]" />
            Allow promo + membership stacking (not recommended)
          </label>
        </div>
      </div>

      <Button data-testid="save-vip-settings" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Save className="w-4 h-4 mr-2" /> Save Settings</Button>
    </div>
  );
};

export default AdminVipSettings;
